import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Calendar, ShieldCheck } from "lucide-react";
import { posts } from "@/data/blog";

const CAT_COLOR = {
  Guide: "text-[#10B981] border-[#10B981]/30 bg-[#10B981]/[0.08]",
  Policy: "text-[#D4AF37] border-[#D4AF37]/30 bg-[#D4AF37]/[0.08]",
  "Case Study": "text-white border-white/25 bg-white/[0.05]",
};

export default function BlogIndex() {
  useEffect(() => {
    document.title = "Blog — Go Higher | Online Reputation Management";
    const meta = document.querySelector('meta[name="description"]');
    const desc =
      "Guides, case studies, and Google policy deep-dives on removing fake reviews and rebuilding a 5-star reputation.";
    if (meta) meta.setAttribute("content", desc);
    else {
      const m = document.createElement("meta");
      m.name = "description";
      m.content = desc;
      document.head.appendChild(m);
    }
  }, []);

  const [featured, ...rest] = posts;

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white">
      <header className="border-b border-white/10 sticky top-0 z-40 bg-[#0A0F1C]/85 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="blog-back-home"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Home
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#10B981] to-[#0e8f66] grid place-items-center">
              <ShieldCheck className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-lg font-bold tracking-tight">
              Go <span className="text-[#D4AF37]">Higher</span>
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
          The Go Higher Journal
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-3 leading-[1.05] max-w-3xl">
          Reputation, mapped to each platform's actual rules.
        </h1>
        <p className="mt-6 text-white/60 max-w-2xl text-lg leading-relaxed">
          Field-tested playbooks for ethical review removal across Google,
          Reddit, Glassdoor, Airbnb, and Trustpilot — plus rating-recovery
          case studies. No fluff, no black-hat tactics.
        </p>

        {/* Featured */}
        <Link
          to={`/blog/${featured.slug}`}
          data-testid={`blog-post-${featured.slug}`}
          className="group mt-14 grid lg:grid-cols-5 gap-8 rounded-3xl overflow-hidden border border-white/10 hover:border-white/20 bg-white/[0.02] transition-colors"
        >
          <div className="lg:col-span-2 aspect-[4/3] lg:aspect-auto overflow-hidden">
            <img
              src={featured.hero_image}
              alt={featured.title}
              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
              loading="eager"
            />
          </div>
          <div className="lg:col-span-3 p-8 lg:p-12 flex flex-col justify-center">
            <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-widest">
              <span className={`inline-flex items-center border rounded-full px-2.5 py-1 ${CAT_COLOR[featured.category]}`}>
                {featured.category}
              </span>
              <span className="text-white/40 inline-flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(featured.date)}
              </span>
              <span className="text-white/40 inline-flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {featured.read_min} min read
              </span>
            </div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold mt-4 leading-tight group-hover:text-[#D4AF37] transition-colors">
              {featured.title}
            </h2>
            <p className="mt-4 text-white/60 leading-relaxed">
              {featured.excerpt}
            </p>
            <div className="mt-6 inline-flex items-center gap-2 text-[#10B981] font-semibold text-sm group-hover:gap-3 transition-all">
              Read the guide
              <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </Link>

        {/* Grid */}
        <div className="mt-16 grid gap-8 md:grid-cols-2">
          {rest.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-post-${p.slug}`}
              className="group rounded-2xl overflow-hidden border border-white/10 hover:border-white/25 bg-white/[0.02] transition-colors"
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src={p.hero_image}
                  alt={p.title}
                  className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest">
                  <span className={`inline-flex items-center border rounded-full px-2 py-0.5 ${CAT_COLOR[p.category]}`}>
                    {p.category}
                  </span>
                  <span className="text-white/40">{formatDate(p.date)}</span>
                  <span className="text-white/40">·</span>
                  <span className="text-white/40">{p.read_min} min</span>
                </div>
                <h3 className="font-display text-xl font-bold mt-3 leading-tight group-hover:text-[#D4AF37] transition-colors">
                  {p.title}
                </h3>
                <p className="mt-2 text-white/55 text-sm leading-relaxed line-clamp-3">
                  {p.excerpt}
                </p>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-20 rounded-2xl border border-[#10B981]/20 bg-gradient-to-br from-[#10B981]/[0.05] to-transparent p-8 lg:p-10">
          <div className="text-xs uppercase tracking-[0.2em] text-[#10B981] font-semibold">
            Ready to see your own removable reviews?
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold mt-2 leading-tight">
            Get a free audit — we'll map every removable review before you spend a dollar.
          </h2>
          <Link
            to="/#audit"
            className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#10B981] hover:bg-[#0ea774] text-white font-semibold text-sm shadow-lg shadow-emerald-500/25 transition-colors"
          >
            Request my free audit
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </main>

      <footer className="border-t border-white/10 mt-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-6 text-xs text-white/40 flex items-center justify-between flex-wrap gap-3">
          <div>© {new Date().getFullYear()} Go Higher, Inc.</div>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
